import { apiFetch } from "@/lib/api";
import { hubDevCoordinatorApiBase } from "@/lib/devCoordinatorOrigin";
import { DEV_PRIMARY_UI_PORT, devPrimaryUiOrigin } from "@/lib/devPrimaryOrigin";

export interface WikiOAuthStatus {
  configured: boolean;
  connected: boolean;
  expiresAt: string | null;
}

export interface WikiSearchResult {
  id: string;
  title: string;
  url: string | null;
  context: string | null;
  collectionName: string | null;
  updatedAt: string | null;
  ranking: number | null;
}

export interface WikiSearchResponse {
  results: WikiSearchResult[];
}

export interface WikiDocument {
  id: string;
  title: string;
  text: string;
  url: string | null;
  collectionName: string | null;
  updatedAt: string | null;
  createdAt: string | null;
}

/** True on the primary hub UI — gateway path mount `/5175/` or direct `:5175`. */
export function isDevPrimaryHubUiOrigin(): boolean {
  if (!import.meta.env.DEV || typeof window === "undefined") {
    return false;
  }
  const { hostname, port, pathname } = window.location;
  if (hostname !== "localhost" && hostname !== "127.0.0.1") {
    return false;
  }
  const primaryInternalPort = "5175";
  if (port === primaryInternalPort) {
    return true;
  }
  if (port === DEV_PRIMARY_UI_PORT || port === "") {
    return pathname === `/${primaryInternalPort}` || pathname.startsWith(`/${primaryInternalPort}/`);
  }
  return false;
}

/** @deprecated Prefer isDevPrimaryHubUiOrigin — bare 5173/ is now the stack operator. */
export function isDevGatewayUiOrigin(): boolean {
  return isDevPrimaryHubUiOrigin();
}

/**
 * Outline OAuth status/start hit the dev login coordinator (8122 via gateway 5173)
 * so every branch stack shares one connection row without depending on branch API reloads.
 */
export function wikiOAuthApiBase(): string {
  return hubDevCoordinatorApiBase();
}

/**
 * After Outline's callback on the coordinator, return to this UI origin.
 */
export function wikiOAuthUiOrigin(): string {
  if (typeof window === "undefined") {
    return devPrimaryUiOrigin();
  }
  return window.location.origin;
}

export async function fetchWikiOAuthStatus(): Promise<WikiOAuthStatus> {
  const base = wikiOAuthApiBase();
  return apiFetch<WikiOAuthStatus>(`${base}/api/wiki/oauth/status`);
}

export async function startWikiOAuth(returnTo: string): Promise<{ authorizeUrl: string; expiresAt: string }> {
  const pathReturnTo = returnTo.startsWith("/") ? returnTo : `/${returnTo}`;
  const base = wikiOAuthApiBase();
  return apiFetch<{ authorizeUrl: string; expiresAt: string }>(`${base}/api/wiki/oauth/start`, {
    method: "POST",
    body: {
      returnTo: pathReturnTo,
      uiOrigin: wikiOAuthUiOrigin(),
    },
  });
}

export async function searchWiki(
  query: string,
  limit = 8,
  options: { signal?: AbortSignal } = {},
): Promise<WikiSearchResponse> {
  return apiFetch<WikiSearchResponse>("/api/wiki/search", {
    query: { q: query, limit },
    signal: options.signal,
  });
}

export async function fetchWikiDocument(id: string): Promise<{ document: WikiDocument }> {
  return apiFetch<{ document: WikiDocument }>(`/api/wiki/documents/${encodeURIComponent(id)}`);
}
